import {FormElement} from "../../element-types/form-element.js";

export class SubmitHandler extends FormElement {
    form;
    type;
    label;

    method;
    url;
    alignment;

    componentTag = "button";
    buttonType = "button";

    submitButton;

    /**
     * Constructs an instance of the class with the provided parameters.
     *
     * @param {Object} formData - The data object containing the form details.
     * @param {HTMLElement} parentElement - The parent DOM element where the instance will be rendered or attached.
     * @param {Array} collection - A collection of items associated with the instance.
     * @return {Object} An instance of the class.
     */
    constructor(formData, parentElement, collection) {
        super(formData, parentElement, collection);
    }

    /**
     * Sets the label for the given component by appending a text node.
     * If the label is undefined or null, it defaults to "Submit".
     *
     * @param {Object} _ - An unused parameter.
     * @param {Object} component - The target component to which the label will be appended.
     * @return {void} This method does not return any value.
     */
    setLabel(_, component) {
        component.appendChild(document.createTextNode(this.label ?? "Submit"));
    }

    /**
     * Registers the default listeners for the provided component.
     *
     * @param {Object} component - The component to which the default listeners are attached.
     * @return {void} This method does not return a value.
     */
    defaultListeners(component) {
        this.subscribeToChanges();
    }

    /**
     * Triggers the necessary actions to handle a change event.
     * Specifically, this method enables the submit functionality.
     *
     * @return {void} Does not return a value.
     */
    changeEvent() {
        this.enableSubmit();
    }

    /**
     * Enables or disables the submit button based on the validity of the associated collection.
     * Adjusts the button's disabled state, cursor style, and tooltip accordingly.
     *
     * @return {void} No return value.
     */
    enableSubmit() {
        const isValid = this.collection.isValid;
        this.submitButton.disabled = !isValid;
        this.submitButton.style.cursor = isValid ? 'pointer' : 'not-allowed';
        this.submitButton.title = isValid ? 'Submit Form' : 'There are errors in your form, please review before submitting.';
    }

    /**
     * Wraps the component inside an outer container element and adds a region-specific container for buttons.
     * The alignment of the button container can be customized through the `alignment` property.
     *
     * @return {HTMLElement} The region-specific container element for buttons.
     */
    componentWrapper() {
        const outerWrapper =  super.componentWrapper();
        outerWrapper.classList.add("action-container");

        const regionWrapper  = document.createElement('div');
        regionWrapper.classList.add('button-container');

        regionWrapper.style.justifyContent = `${this.alignment ?? "center"}`;

        outerWrapper.appendChild(regionWrapper);
        return regionWrapper;
    }

    /**
     * Creates and configures a submit button component.
     * Initializes the submit button with a specific type and enables it for submission.
     *
     * @return {Object} The configured submit button component.
     */
    component() {
        this.submitButton = super.component();
        this.submitButton.type = this.buttonType;
        this.enableSubmit();

        return this.submitButton;
    }
}
